import DateFnsUtils from '@date-io/date-fns';
import { Button, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { trackPromise } from 'react-promise-tracker';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import axios from '../../../axios/AxiosInterceptors';
import { onError, onSuccess } from '../../../store/actions/popupActions';
import useStyles from "../../../Styles";
import { hasPermissionForAccount } from '../../../Utility/PermissionsUtil';
import { StickyTableCell } from '../../StickyTable/StickyTable';

const GenerationMeterValues = (props) => {

    const classes = useStyles(props);
    const params = useParams();
    const dispatch = useDispatch();
    const roles = useSelector(state => state.auth.roles);
    const [selectedMeterAccount, setSelectedMeterAccount] = useState({ id: 0, meterName: '' });
    const [meterAccounts, setMeterAccounts] = useState([{ id: 0, meterName: '' }]);
    const [meterValues, setMeterValues] = useState([]);
    const [startDate, setStartDate] = useState(moment().subtract(1, 'days'));
    const [endDate, setEndDate] = useState(moment().subtract(1, 'days'));
    const [editableDays, setEditableDays] = useState([]);

    useEffect(() => {
        retrieveMeterAccounts();
        retrieveEditableDays();
        setMeterValues([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params])

    const retrieveMeterAccounts = async () => {
        await trackPromise(axios.get("/powermeter/v1/account/" + params.accountId + "/meter").then(response => {
            if (response && response.data) {
                setMeterAccounts([{ id: 0, meterName: '' }, ...response.data.filter(r => r.id)]);
            }
        })).catch(error => {
            dispatch(onError(error.response));
        })
    }

    const retrieveEditableDays = async () => {
        if (hasPermissionForAccount(roles, Number(params.accountId), "ROLE_submit_meter_values")) {
            await trackPromise(axios.get("/pjm/powermeter/submission/window").then(response => {
                setEditableDays(response.data);
            })).catch(error => {
                setEditableDays([]);
                dispatch(onError(error.response));
            })
        } else {
            setEditableDays([]);
        }
    }

    const handleMeterAccountChange = (e, value) => {
        setSelectedMeterAccount(value);
    }

    const retrieveMeterValues = async () => {
        if (selectedMeterAccount && selectedMeterAccount.id !== 0) {
            await trackPromise(axios.get("/powermeter/v3/account/" + params.accountId + "/hourly/meter/" + selectedMeterAccount.id + "?startDate=" + moment(startDate).format("YYYY-MM-DD") + "&endDate=" + moment(endDate).format("YYYY-MM-DD")).then(response => {
                mapMeterValues(response);
            })).catch(error => {
                dispatch(onError(error));
            });
        } else {
            await trackPromise(axios.get("/powermeter/v3/account/" + params.accountId + "/hourly?startDate=" + moment(startDate).format("YYYY-MM-DD") + "&endDate=" + moment(endDate).format("YYYY-MM-DD")).then(response => {
                mapMeterValues(response);
            })).catch(error => {
                dispatch(onError(error));
            });
        }
    }

    const mapMeterValues = (response) => {
        const values = [];
        let meterDataByGenerator = new Map();
        if (response && response.data && response.data.results) {
            meterDataByGenerator = response.data.results.reduce((accumulator, r) => {
                if (!accumulator.has(r.meterAccountId)) {
                    accumulator.set(r.meterAccountId, []);
                }
                accumulator.get(r.meterAccountId).push(r);
                return accumulator;
            }, new Map());
        }
        const totalDays = Math.round(moment(endDate).diff(moment(startDate), 'days', true)) + 1;
        for (let i = 0; i < (totalDays > 0 ? totalDays : 1); i++) {
            const date = moment(startDate).add(i, 'days');
            meterAccounts.filter(r => ((selectedMeterAccount && selectedMeterAccount.id !== 0) ? r.id === selectedMeterAccount.id : r.id !== 0)).forEach(r => {
                const hourlyValues = [...Array(24).keys()].map(j => { return calculateMeterValue(meterDataByGenerator, r.id, date, j) });
                const total = hourlyValues.filter(r => r.value !== '-').reduce((acc, r) => Number(acc) + Number(r.value), 0).toFixed(3);
                values.push({ meterAccountId: r.id, pjmMeterAccountId: r.powerMeterId, generatorId: r.generatorId, date: date.format("YYYY-MM-DD"), meterName: r.meterName, total: total, values: hourlyValues });
            })
        }
        setMeterValues([...values]);
    }


    const mapToHourlyMeterValue = () => {
        const hourlyMeterValues = [];
        const updatedValues = meterValues.filter(r => editableDays.find(t => t === r.date));
        if (updatedValues && updatedValues.length > 0) {
            updatedValues.forEach((updatedValue) => {
                updatedValue.values.forEach((hourlyValue) => {
                    let value = {
                        accountId: Number(params.accountId),
                        meterAccountId: Number(updatedValue.meterAccountId),
                        pjmMeterAccountId: Number(updatedValue.pjmMeterAccountId),
                        generatorId: updatedValue.generatorId,
                        meterAccountName: updatedValue.meterName,
                        originalValue: Number(hourlyValue.value),
                        hourBeginningDateTime: {
                            hourBeginningDate: updatedValue.date,
                            hourBeginning: hourlyValue.hourBeginning
                        }
                    }
                    hourlyMeterValues.push(value);
                }
                )
            });
        }
        return hourlyMeterValues;
    }

    const saveHourlyMeterValues = async () => {
        const hourlyMeterValues = mapToHourlyMeterValue();
        const valuesByMeterAccountId = Map.groupBy(hourlyMeterValues, (hourlyMeterValue) => hourlyMeterValue.meterAccountId);
        valuesByMeterAccountId.forEach(async (value, key, map) => {
            await trackPromise(axios.post("/powermeter/v3/account/" + params.accountId + "/hourly/meter/" + key, JSON.stringify(value)).then(response => {
                dispatch(onSuccess("Saved hourly meter values"));
                mapMeterValues(response.data);
            }).catch(error => {
                dispatch(onError(error.response));
            }))
        });
    }

    const handlePowerMeterValueChange = (e, date, index, meterAccountId) => {
        for (let i = 0; i < meterValues.length; i++) {
            if (meterValues[i].date === date && meterValues[i].meterAccountId === meterAccountId) {
                meterValues[i].values[index].value = e.value;
                meterValues[i].total = meterValues[i].values.reduce((acc, r) => Number(acc) + Number(r.value), 0).toFixed(3);
            }
        }
        setMeterValues([...meterValues]);
    }

    const renderMeterData = () => {
        const tableRows = [];
        if (meterValues) {
            tableRows.push(meterValues.map(r => {
                return (
                    <TableRow key={r.meterAccountId + r.date}>
                        <StickyTableCell>
                            <TableCell>{r.date}</TableCell>
                            <TableCell>{r.pjmMeterAccountId}</TableCell>
                            <TableCell>{r.meterName}</TableCell>
                            <TableCell style={{ minWidth: 125, maxWidth: 200 }}>
                                <NumberFormat value={r.total} customInput={TextField} thousandSeparator={true} decimalScale={3} type="text"
                                    disabled={true} fixedDecimalScale /></TableCell>
                        </StickyTableCell>
                        {renderHourlyValues(r.values, r.date, r.meterAccountId)}
                    </TableRow>
                )
            }))
        };
        return tableRows;
    }

    const calculateMeterValue = (meterDataByGenerator, meterAccountId, date, hourBeginning) => {
        let meterValue = meterDataByGenerator.get(meterAccountId);
        if (meterValue) {
            meterValue = meterValue.filter(t => t.hourBeginningDateTime.hourBeginningDate === date.format("YYYY-MM-DD") && t.hourBeginningDateTime.hourBeginning === hourBeginning);
            if (meterValue && meterValue.length > 0) {
                return { hourBeginning: hourBeginning, value: Number(meterValue[0].originalValue).toFixed(3) };
            }
        }
        return { hourBeginning: hourBeginning, value: Number(0).toFixed(3) };
    }

    const renderHourlyValues = (values, date, meterAccountId) => {
        let hourlyValues = [];
        const isEditable = editableDays.find(r => r === date) && hasPermissionForAccount(roles, Number(params.accountId), "ROLE_submit_meter_values");
        for (let i = 0; i < values.length; i++) {
            if (isEditable) {
                hourlyValues.push(<TableCell key={i + date}>
                    <NumberFormat value={values[i].value} customInput={TextField} thousandSeparator={true} decimalScale={3} type="text"
                        onValueChange={(e) => handlePowerMeterValueChange(e, date, i, meterAccountId)} disabled={false} fixedDecimalScale />
                </TableCell>)
            } else {
                hourlyValues.push(<TableCell key={i + date}>{values[i].value}</TableCell>)
            }
        }
        return hourlyValues;
    }


    return (
        <main className={classes.layout}>
            <Typography variant="h4" align='center'>Generation Meters</Typography>
            <Grid container spacing={2}>
                <Grid item xs={6} sm={1} />
                <Grid item xs={6} sm={2}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker variant="inline" disableToolbar onChange={(e) => setStartDate(e)} value={startDate} autoOk={true} margin="normal" format="yyyy-MM-dd" />
                    </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={6} sm={1} />
                <Grid item xs={6} sm={2}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker variant="inline" disableToolbar onChange={(e) => setEndDate(e)} minDate={startDate} value={endDate} autoOk={true} margin="normal" format="yyyy-MM-dd" />
                    </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={6} sm={1} />
                <Grid item xs={6} sm={2} >
                    <Autocomplete value={selectedMeterAccount} onChange={(e, value) => handleMeterAccountChange(e, value)} options={meterAccounts}
                        getOptionLabel={(option) => option.meterName} renderInput={(params) => <TextField {...params} label="Meter Accounts" />}
                        getOptionSelected={(option, value) => option.id === value.id} />
                </Grid>
                <Grid item xs={6} sm={1} />
                <Grid item xs={6} sm={2}>
                    <Button onClick={retrieveMeterValues}>Retrieve</Button>
                </Grid>
            </Grid>
            <TableContainer style={{ maxHeight: 600, tableLayout: "fixed", marginBottom: 20 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <StickyTableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>PJM Meter ID</TableCell>
                                <TableCell>Meter Name</TableCell>
                                <TableCell style={{ minWidth: 125, maxWidth: 200 }}>Total (MWhs)</TableCell>
                            </StickyTableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 1 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 2 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 3 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 4 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 5 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 6 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 7 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 8 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 9 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 10 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 11 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 12 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 13 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 14 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 15 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 16 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 17 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 18 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 19 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 20 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 21 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 22 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 23 (MWhs)</TableCell>
                            <TableCell style={{ minWidth: 100 }}>Hour Ending 24 (MWhs)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderMeterData()}
                    </TableBody>
                </Table>
            </TableContainer>
            <Grid container spacing={2}>
                <Grid item xs={4} />
                <Grid item xs={4} align='center'>
                    <Button onClick={saveHourlyMeterValues}>Submit</Button>
                </Grid>
                <Grid item xs={4} />
            </Grid>
        </main>
    )

}

export default GenerationMeterValues;